/**
 * ErrorList  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";
import { ErrorListItem } from "./ErrorListItem";
import { BEM } from "@adapptio/lib-components";
import {
	DOC_ERROR_SEVERITY,
	ICompileError,
	IDocumentError,
	IRuntimeError
} from "@adapptio/lib-blueprint";
import { ErrorListCategoryTitle } from "./ErrorListCategoryTitle";
import { VirtualList } from "../VirtualList";
import {
	ERROR_LIST_CATEGORY,
	ERROR_LIST_ITEM_DATA_TYPE,
	IErrorListGroup,
	IErrorListItemCategoryData,
	IErrorListItemErrorData,
	OnErrorItemClick
} from "./ErrorListTypes";
import { getErrorsData } from "./getErrorListData";
import { PanelNote } from "../Panel";

export interface IErrorListProps {
	errorGroups?: IErrorListGroup[];
	onCompileErrorClick?: OnErrorItemClick<ICompileError>;
	onParseErrorClick?: OnErrorItemClick<IDocumentError>;
	onRuntimeErrorClick?: OnErrorItemClick<IRuntimeError>;
	category?: ERROR_LIST_CATEGORY;
	severityFilter?: Set<DOC_ERROR_SEVERITY> | null;
	className?: string;
}

export const ErrorList: React.FC<IErrorListProps> = ({
	errorGroups,
	onCompileErrorClick,
	onParseErrorClick,
	onRuntimeErrorClick,
	category,
	severityFilter,
	className
}) => {
	const baseClass = BEM("edt-error-list", null, {
		category
	});
	baseClass.add("edt-base");

	const insertCategoryTitles = category === ERROR_LIST_CATEGORY.ALL;

	const data = getErrorsData(errorGroups, {
		severityFilter,
		insertCategoryTitles,
		onRuntimeErrorClick,
		onParseErrorClick,
		onCompileErrorClick
	});

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()}>
			<div className={baseClass.element("container").toClassName()}>
				{data.length ? (
					<VirtualList
						totalCount={data.length}
						overscan={400}
						itemContent={(index) =>
							({
								[ERROR_LIST_ITEM_DATA_TYPE.CATEGORY_TITLE]: (
									<ErrorListCategoryTitle
										{...(data[index] as IErrorListItemCategoryData)?.value}
									/>
								),
								[ERROR_LIST_ITEM_DATA_TYPE.ERROR]: (
									<ErrorListItem {...(data[index] as IErrorListItemErrorData)?.value} />
								)
							}[data[index]?.type] || "some")
						}
					/>
				) : (
					<PanelNote>No messages</PanelNote>
				)}
			</div>
		</div>
	);
};

ErrorList.defaultProps = {
	category: ERROR_LIST_CATEGORY.ALL
};
